import React, { useState, useEffect } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { doc, getDoc } from 'firebase/firestore';
import { db } from '../firebase';
import { payoutService } from '../services/payoutService';
import { BankDetails, PayoutRequest, PayoutConfig } from '../types/payout';
import { 
  DollarSign, 
  Banknote, 
  TrendingUp, 
  AlertCircle,
  CheckCircle,
  MessageSquare,
  Clock,
  CreditCard,
  Save,
  Calendar,
  Building2,
  Eye,
  EyeOff,
  Pencil,
  Trash2,
  Plus,
  FileText,
  ExternalLink,
  CheckCircle2,
} from 'lucide-react';
import { Link } from 'react-router-dom';
import { toast } from 'react-hot-toast';
import { formatNumber } from '../util/formatters';

const PayoutsScreen: React.FC = () => {
  const { user } = useAuth();
  const [earnings, setEarnings] = useState(0);
  const [totalEarnings, setTotalEarnings] = useState(0);
  const [bankDetails, setBankDetails] = useState<BankDetails>({
    accountHolderName: '',
    iban: '',
    bankName: '',
    swiftCode: '',
    country: '',
  });
  const [payoutHistory, setPayoutHistory] = useState<PayoutRequest[]>([]);
  const [config, setConfig] = useState<PayoutConfig>({ commissionRate: 20, minPayoutAmount: 100 });
  const [loading, setLoading] = useState(true);
  const [requesting, setRequesting] = useState(false);
  const [savingBankDetails, setSavingBankDetails] = useState(false);
  const [selectedPayout, setSelectedPayout] = useState<PayoutRequest | null>(null);
  const [messageText, setMessageText] = useState('');
  const [showBankDetails, setShowBankDetails] = useState(false);
  const [isAddBankOpen, setIsAddBankOpen] = useState(false);
  const [isWithdrawOpen, setIsWithdrawOpen] = useState(false);
  const [withdrawAmount, setWithdrawAmount] = useState('');
  const [expandedTransaction, setExpandedTransaction] = useState<string | null>(null);

  useEffect(() => {
    if (user) {
      fetchData();
    }
  }, [user]);

  const fetchData = async () => {
    if (!user) return;
    
    setLoading(true);
    try {
      // Fetch user earnings
      const userDoc = await getDoc(doc(db, 'users', user.uid));
      if (userDoc.exists()) {
        const userData = userDoc.data();
        setEarnings(userData.earnings || 0);
        setTotalEarnings(userData.totalEarnings || 0);
        
        if (userData.bankDetails) {
          setBankDetails(userData.bankDetails);
        }
      }

      // Fetch payout history
      const history = await payoutService.getPayoutHistory(user.uid);
      setPayoutHistory(history);

      // Fetch config
      const configData = await payoutService.getConfig();
      setConfig(configData);
    } catch (error) {
      console.error('Error fetching payout data:', error);
      toast.error('Failed to load payout information');
    } finally {
      setLoading(false);
    }
  };

  const handleRequestPayout = async () => {
    if (!user) return;

    if (!bankDetails.accountHolderName || !bankDetails.iban) {
      toast.error('Please fill in your bank details first');
      return;
    }

    if (earnings < config.minPayoutAmount) {
      toast.error(`Minimum payout amount is $${config.minPayoutAmount}`);
      return;
    }

    setRequesting(true);
    try {
      const result = await payoutService.requestPayout(user.uid, bankDetails);
      
      if (result.success) {
        toast.success('Payout requested successfully!');
        await fetchData(); // Refresh data
      } else {
        toast.error(result.message);
      }
    } catch (error) {
      toast.error('Failed to request payout');
    } finally {
      setRequesting(false);
    }
  };

  const handleSaveBankDetails = async () => {
    if (!user) return;

    if (!bankDetails.accountHolderName || !bankDetails.iban) {
      toast.error('Account holder name and IBAN are required');
      return;
    }

    setSavingBankDetails(true);
    try {
      const userRef = doc(db, 'users', user.uid);
      await getDoc(userRef); // Just to validate
      
      // Bank details will be saved when requesting payout
      toast.success('Bank details saved successfully!');
    } catch (error) {
      toast.error('Failed to save bank details');
    } finally {
      setSavingBankDetails(false);
    }
  };

  const handleSendMessage = async (payoutId: string) => {
    if (!user || !messageText.trim()) return;

    try {
      const result = await payoutService.addMessage(payoutId, user.uid, messageText);
      
      if (result.success) {
        toast.success('Message sent');
        setMessageText('');
        await fetchData();
      } else {
        toast.error(result.message);
      }
    } catch (error) {
      toast.error('Failed to send message');
    }
  };

  const handleMarkAsClaimed = async (payoutId: string) => {
    if (!user) return;

    try {
      const result = await payoutService.markAsClaimed(payoutId, user.uid);
      
      if (result.success) {
        toast.success('Marked as claimed');
        await fetchData();
      } else {
        toast.error(result.message);
      }
    } catch (error) {
      toast.error('Failed to mark as claimed');
    }
  };

  const handleWithdraw = async () => {
    if (!user || !withdrawAmount) return;

    const amount = parseFloat(withdrawAmount);
    if (amount <= 0 || amount > earnings) {
      toast.error('Invalid withdrawal amount');
      return;
    }

    setRequesting(true);
    try {
      const result = await payoutService.requestPayout(user.uid, bankDetails);
      
      if (result.success) {
        toast.success('Withdrawal requested successfully!');
        setIsWithdrawOpen(false);
        setWithdrawAmount('');
        await fetchData();
      } else {
        toast.error(result.message);
      }
    } catch (error) {
      toast.error('Failed to request withdrawal');
    } finally {
      setRequesting(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-b from-gray-50 to-gray-100 flex items-center justify-center">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-purple-600"></div>
      </div>
    );
  }

  // Mock monthly earnings data - replace with actual data
  const monthlyEarnings = [
    { month: "Jan", amount: totalEarnings * 0.1 },
    { month: "Feb", amount: totalEarnings * 0.12 },
    { month: "Mar", amount: totalEarnings * 0.09 },
    { month: "Apr", amount: totalEarnings * 0.15 },
    { month: "May", amount: totalEarnings * 0.17 },
    { month: "Jun", amount: earnings },
  ];

  const thisMonthEarnings = earnings;
  const lastMonthEarnings = totalEarnings * 0.17;
  const percentChange = lastMonthEarnings > 0 
    ? ((thisMonthEarnings - lastMonthEarnings) / lastMonthEarnings * 100).toFixed(1)
    : '0';

  return (
    <div className="min-h-screen bg-gradient-to-b from-gray-50 to-gray-100 p-4 md:p-6 lg:p-8">
      <div className="max-w-7xl mx-auto space-y-6">
        {/* Header */}
        <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
          <div>
            <h1 className="text-2xl md:text-3xl font-bold text-gray-900">Earnings & Payments</h1>
            <p className="text-gray-500 mt-1">Track your earnings and manage payouts</p>
          </div>
          <div className="flex gap-3">
            <button
              onClick={() => setIsWithdrawOpen(true)}
              className="flex items-center gap-2 px-4 py-2 bg-gradient-to-r from-purple-600 to-pink-600 text-white rounded-lg hover:opacity-90 transition-opacity"
            >
              <DollarSign className="h-4 w-4" />
              Withdraw Funds
            </button>
          </div>
        </div>

        {/* Stats Overview */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
          <div className="bg-white border border-gray-200 rounded-lg p-6 hover:shadow-md transition-shadow">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium text-gray-500">Total Earnings</p>
                <p className="text-2xl font-bold text-gray-900 mt-1">
                  ${totalEarnings.toLocaleString()}
                </p>
                <p className="text-xs text-gray-500 mt-1">All time</p>
              </div>
              <div className="h-12 w-12 rounded-full bg-gradient-to-br from-purple-100 to-purple-200 flex items-center justify-center">
                <DollarSign className="h-6 w-6 text-purple-600" />
              </div>
            </div>
          </div>

          <div className="bg-white border border-gray-200 rounded-lg p-6 hover:shadow-md transition-shadow">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium text-gray-500">Available Balance</p>
                <p className="text-2xl font-bold text-green-600 mt-1">
                  ${earnings.toLocaleString()}
                </p>
                <p className="text-xs text-gray-500 mt-1">Ready to withdraw</p>
              </div>
              <div className="h-12 w-12 rounded-full bg-green-100 flex items-center justify-center">
                <CreditCard className="h-6 w-6 text-green-600" />
              </div>
            </div>
          </div>

          <div className="bg-white border border-gray-200 rounded-lg p-6 hover:shadow-md transition-shadow">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium text-gray-500">This Month</p>
                <p className="text-2xl font-bold text-gray-900 mt-1">
                  ${thisMonthEarnings.toLocaleString()}
                </p>
                <div className="flex items-center gap-1 mt-1">
                  <TrendingUp className="h-3 w-3 text-green-500" />
                  <p className="text-xs text-green-500">+{percentChange}% vs last month</p>
                </div>
              </div>
              <div className="h-12 w-12 rounded-full bg-blue-100 flex items-center justify-center">
                <TrendingUp className="h-6 w-6 text-blue-600" />
              </div>
            </div>
          </div>

          <div className="bg-white border border-gray-200 rounded-lg p-6 hover:shadow-md transition-shadow">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium text-gray-500">Platform Fee</p>
                <p className="text-2xl font-bold text-gray-900 mt-1">
                  {config.commissionRate}%
                </p>
                <p className="text-xs text-gray-500 mt-1">You earn {100 - config.commissionRate}%</p>
              </div>
              <div className="h-12 w-12 rounded-full bg-orange-100 flex items-center justify-center">
                <Calendar className="h-6 w-6 text-orange-600" />
              </div>
            </div>
          </div>
        </div>

        {/* Main Content */}
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          {/* Left Column - Earnings Chart & Recent Transactions */}
          <div className="lg:col-span-2 space-y-6">
            {/* Earnings Chart */}
            <div className="bg-white border border-gray-200 rounded-lg">
              <div className="p-6 pb-0">
                <h3 className="text-lg font-semibold text-gray-900">Earnings Overview</h3>
                <p className="text-sm text-gray-500 mt-1">Your earnings over the last 6 months</p>
              </div>
              <div className="p-6">
                <div className="h-64 flex items-end justify-between gap-2 px-4">
                  {monthlyEarnings.map((item, index) => {
                    const maxAmount = Math.max(...monthlyEarnings.map(e => e.amount), 1);
                    // Calculate height proportionally
                    const heightPercent = (item.amount / maxAmount) * 100;
                    // Use pixels for better control - 256px is h-64
                    const heightPx = Math.max((item.amount / maxAmount) * 240, 4);
                    return (
                      <div key={item.month} className="flex flex-col items-center flex-1 gap-2">
                        <span className="text-sm font-medium text-gray-700">${formatNumber(Math.round(item.amount))}</span>
                        <div 
                          className={`w-full rounded-t-lg transition-all hover:opacity-80 ${
                            index === monthlyEarnings.length - 1 
                              ? 'bg-gradient-to-r from-purple-600 to-pink-600' 
                              : 'bg-gray-200'
                          }`}
                          style={{ height: `${heightPx}px` }}
                        />
                        <span className="text-xs text-gray-500">{item.month}</span>
                      </div>
                    );
                  })}
                </div>
              </div>
            </div>

            {/* Recent Transactions / Payout History */}
            <div className="bg-white border border-gray-200 rounded-lg">
              <div className="p-6 pb-0">
                <h3 className="text-lg font-semibold text-gray-900">Recent Transactions</h3>
                <p className="text-sm text-gray-500 mt-1">Your latest earnings and payouts</p>
              </div>
              <div className="divide-y divide-gray-100">
                {payoutHistory.length === 0 ? (
                  <div className="text-center py-12">
                    <Banknote className="w-16 h-16 text-gray-300 mx-auto mb-4" />
                    <p className="text-gray-500">No transactions yet</p>
                  </div>
                ) : (
                  payoutHistory.map((payout) => (
                    <div key={payout.id} className="hover:bg-gray-50 transition-colors">
                      <div 
                        className="flex items-center justify-between p-4 cursor-pointer"
                        onClick={() => setExpandedTransaction(
                          expandedTransaction === payout.id ? null : payout.id
                        )}
                      >
                        <div className="flex items-center gap-4">
                          <div className={`h-10 w-10 rounded-full flex items-center justify-center ${
                            payout.status === 'completed' ? 'bg-green-100' : 'bg-orange-100'
                          }`}>
                            {payout.status === 'completed' ? (
                              <CheckCircle2 className="h-5 w-5 text-green-600" />
                            ) : (
                              <Clock className="h-5 w-5 text-orange-600" />
                            )}
                          </div>
                          <div>
                            <p className="font-medium text-gray-900">Payout Request</p>
                            <p className="text-sm text-gray-500">{payout.requestedAt.toLocaleDateString()}</p>
                          </div>
                        </div>
                        <div className="flex items-center gap-4">
                          <div className="text-right">
                            <p className="font-semibold text-gray-900">
                              -${formatNumber(parseFloat(payout.amount.toFixed(2)))}
                            </p>
                            <div className="flex items-center gap-1 justify-end">
                              {payout.status === 'completed' ? (
                                <CheckCircle2 className="h-3 w-3 text-green-500" />
                              ) : (
                                <Clock className="h-3 w-3 text-orange-500" />
                              )}
                              <span className={`text-xs capitalize ${
                                payout.status === 'completed' ? 'text-green-500' : 'text-orange-500'
                              }`}>
                                {payout.status}
                              </span>
                            </div>
                          </div>
                        </div>
                      </div>
                      {expandedTransaction === payout.id && (
                        <div className="px-4 pb-4 bg-gray-50 mx-4 mb-4 rounded-lg p-4">
                          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 text-sm mb-4">
                            <div>
                              <p className="text-gray-500">Transaction ID</p>
                              <p className="font-medium text-gray-900">{payout.id}</p>
                            </div>
                            <div>
                              <p className="text-gray-500">Status</p>
                              <p className={`font-medium capitalize ${
                                payout.status === 'completed' ? 'text-green-600' : 'text-orange-600'
                              }`}>{payout.status}</p>
                            </div>
                          </div>
                          
                          {/* Bank Details */}
                          <div className="bg-white rounded-lg p-3 mb-3 border border-gray-200">
                            <p className="text-sm text-gray-600">
                              <strong>Account:</strong> {payout.bankDetails.accountHolderName}
                            </p>
                            <p className="text-sm text-gray-600">
                              <strong>IBAN:</strong> {payout.bankDetails.iban}
                            </p>
                          </div>

                          {/* Admin Proof */}
                          {payout.adminProof && (
                            <div className="bg-green-50 border border-green-200 rounded-lg p-3 mb-3">
                              <p className="text-sm font-medium text-green-900 mb-2">Payment Proof:</p>
                              {payout.adminProof.message && (
                                <p className="text-sm text-green-800">{payout.adminProof.message}</p>
                              )}
                              {payout.adminProof.imageUrl && (
                                <img
                                  src={payout.adminProof.imageUrl}
                                  alt="Payment proof"
                                  className="mt-2 max-w-full h-auto rounded"
                                />
                              )}
                            </div>
                          )}

                          {/* Messages */}
                          {payout.messages && payout.messages.length > 0 && (
                            <div className="border-t pt-3 mt-3">
                              <p className="text-sm font-medium text-gray-700 mb-2">Messages:</p>
                              <div className="space-y-2 max-h-48 overflow-y-auto">
                                {payout.messages.map((msg, idx) => (
                                  <div
                                    key={idx}
                                    className={`p-2 rounded text-sm ${
                                      msg.from === 'admin'
                                        ? 'bg-blue-50 text-blue-900'
                                        : 'bg-gray-100 text-gray-900'
                                    }`}
                                  >
                                    <div className="flex justify-between items-start">
                                      <p className="font-medium">
                                        {msg.from === 'admin' ? 'Admin' : 'You'}:
                                      </p>
                                      <span className="text-xs text-gray-500">
                                        {msg.timestamp.toLocaleString()}
                                      </span>
                                    </div>
                                    <p className="mt-1">{msg.message}</p>
                                  </div>
                                ))}
                              </div>
                            </div>
                          )}

                          {/* Actions */}
                          <div className="flex gap-2 mt-3">
                            {payout.status === 'completed' && !payout.markedAsClaimedByStar && (
                              <button
                                onClick={() => handleMarkAsClaimed(payout.id)}
                                className="flex items-center gap-2 px-3 py-2 bg-green-600 text-white text-sm rounded-lg hover:bg-green-700"
                              >
                                <CheckCircle className="w-4 h-4" />
                                Mark as Claimed
                              </button>
                            )}
                            {selectedPayout?.id !== payout.id && (
                              <button
                                onClick={() => setSelectedPayout(payout)}
                                className="flex items-center gap-2 px-3 py-2 text-purple-600 hover:bg-purple-50 text-sm rounded-lg"
                              >
                                <MessageSquare className="w-4 h-4" />
                                Add Message
                              </button>
                            )}
                          </div>

                          {/* Message Input */}
                          {selectedPayout?.id === payout.id && (
                            <div className="mt-3 flex gap-2">
                              <input
                                type="text"
                                value={messageText}
                                onChange={(e) => setMessageText(e.target.value)}
                                placeholder="Type your message..."
                                className="flex-1 px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent text-sm"
                                onKeyDown={(e) => {
                                  if (e.key === 'Enter') {
                                    handleSendMessage(payout.id);
                                  }
                                }}
                              />
                              <button
                                onClick={() => handleSendMessage(payout.id)}
                                className="px-4 py-2 bg-purple-600 text-white rounded-lg hover:bg-purple-700 text-sm"
                              >
                                Send
                              </button>
                              <button
                                onClick={() => setSelectedPayout(null)}
                                className="px-4 py-2 bg-gray-300 text-gray-700 rounded-lg hover:bg-gray-400 text-sm"
                              >
                                Cancel
                              </button>
                            </div>
                          )}
                        </div>
                      )}
                    </div>
                  ))
                )}
              </div>
            </div>
          </div>

          {/* Right Column - Bank Details & Info */}
          <div className="space-y-6">
            {/* Bank Details */}
            <div className="bg-white border border-gray-200 rounded-lg">
              <div className="p-6 pb-0">
                <div className="flex items-center justify-between mb-1">
                  <h3 className="text-lg font-semibold text-gray-900">Bank Details</h3>
                  <button 
                    onClick={() => setShowBankDetails(!showBankDetails)}
                    className="p-2 hover:bg-gray-100 rounded-lg transition-colors"
                  >
                    {showBankDetails ? (
                      <EyeOff className="h-4 w-4 text-gray-600" />
                    ) : (
                      <Eye className="h-4 w-4 text-gray-600" />
                    )}
                  </button>
                </div>
                <p className="text-sm text-gray-500">Your linked bank account for payouts</p>
              </div>
              <div className="p-6">
                {bankDetails.accountHolderName ? (
                  <div className="space-y-4">
                    <div className="p-4 border border-gray-200 rounded-lg bg-gray-50">
                      <div className="flex items-center gap-3 mb-4">
                        <div className="h-12 w-12 rounded-full bg-blue-100 flex items-center justify-center">
                          <Building2 className="h-6 w-6 text-blue-600" />
                        </div>
                        <div>
                          <p className="font-semibold text-gray-900">{bankDetails.bankName || 'Bank Account'}</p>
                          <p className="text-sm text-gray-500">Checking Account</p>
                        </div>
                      </div>
                      <div className="space-y-3">
                        <div className="flex justify-between items-center">
                          <span className="text-sm text-gray-500">Account Holder</span>
                          <span className="text-sm font-medium text-gray-900">{bankDetails.accountHolderName}</span>
                        </div>
                        <div className="flex justify-between items-center">
                          <span className="text-sm text-gray-500">IBAN</span>
                          <span className="text-sm font-medium text-gray-900 font-mono">
                            {showBankDetails ? bankDetails.iban : '****' + bankDetails.iban.slice(-4)}
                          </span>
                        </div>
                        {bankDetails.swiftCode && (
                          <div className="flex justify-between items-center">
                            <span className="text-sm text-gray-500">SWIFT Code</span>
                            <span className="text-sm font-medium text-gray-900 font-mono">
                              {showBankDetails ? bankDetails.swiftCode : '****' + bankDetails.swiftCode.slice(-4)}
                            </span>
                          </div>
                        )}
                      </div>
                    </div>
                    <div className="flex gap-2">
                      <button 
                        onClick={() => setIsAddBankOpen(true)}
                        className="flex items-center gap-2 flex-1 px-3 py-2 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors text-sm"
                      >
                        <Pencil className="h-3 w-3" />
                        Edit
                      </button>
                    </div>
                  </div>
                ) : (
                  <div className="text-center py-6">
                    <div className="h-16 w-16 rounded-full bg-gray-100 flex items-center justify-center mx-auto mb-4">
                      <Building2 className="h-8 w-8 text-gray-400" />
                    </div>
                    <p className="text-gray-500 mb-4">No bank account linked</p>
                    <button 
                      onClick={() => setIsAddBankOpen(true)}
                      className="flex items-center gap-2 px-4 py-2 bg-gradient-to-r from-purple-600 to-pink-600 text-white rounded-lg hover:opacity-90 transition-opacity mx-auto"
                    >
                      <Plus className="h-4 w-4" />
                      Add Bank Account
                    </button>
                  </div>
                )}
              </div>
            </div>

            {/* Minimum Payout Warning */}
            {earnings < config.minPayoutAmount && (
              <div className="bg-orange-50 border border-orange-200 rounded-lg p-4">
                <div className="flex items-start gap-3">
                  <div className="h-10 w-10 rounded-full bg-orange-100 flex items-center justify-center flex-shrink-0">
                    <AlertCircle className="h-5 w-5 text-orange-600" />
                  </div>
                  <div>
                    <p className="font-medium text-orange-900">Minimum Payout</p>
                    <p className="text-sm text-orange-700 mt-1">
                      Minimum payout amount is ${formatNumber(config.minPayoutAmount)}. Current balance: ${formatNumber(parseFloat(earnings.toFixed(2)))}
                    </p>
                  </div>
                </div>
              </div>
            )}

            {/* Help Card */}
            <div className="bg-white border border-gray-200 rounded-lg p-4">
              <div className="flex items-start gap-3">
                <div className="h-10 w-10 rounded-full bg-blue-100 flex items-center justify-center flex-shrink-0">
                  <AlertCircle className="h-5 w-5 text-blue-600" />
                </div>
                <div>
                  <p className="font-medium text-gray-900">Need Help?</p>
                  <p className="text-sm text-gray-500 mt-1">
                    If you have any issues with payments, please contact our support team.
                  </p>
                  <button className="text-purple-600 hover:text-purple-700 text-sm font-medium mt-2">
                    Contact Support
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Withdraw Funds Modal */}
      {isWithdrawOpen && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-lg max-w-md w-full p-6">
            <h2 className="text-xl font-bold text-gray-900 mb-2">Withdraw Funds</h2>
            <p className="text-sm text-gray-500 mb-4">
              Transfer your available balance to your bank account.
            </p>
            
            <div className="space-y-4">
              <div className="p-4 bg-gray-50 rounded-lg">
                <p className="text-sm text-gray-500">Available Balance</p>
                <p className="text-2xl font-bold text-gray-900">${formatNumber(parseFloat(earnings.toFixed(2)))}</p>
              </div>
              
              <div className="space-y-2">
                <label className="block text-sm font-medium text-gray-700">Amount to Withdraw</label>
                <div className="relative">
                  <DollarSign className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400" />
                  <input
                    type="number"
                    placeholder="0.00"
                    value={withdrawAmount}
                    onChange={(e) => setWithdrawAmount(e.target.value)}
                    className="w-full pl-9 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent"
                  />
                </div>
                <div className="flex gap-2 mt-2">
                  {[25, 50, 75, 100].map((percent) => (
                    <button
                      key={percent}
                      onClick={() => setWithdrawAmount((earnings * (percent / 100)).toFixed(2))}
                      className="flex-1 px-3 py-1 text-sm border border-gray-300 rounded hover:bg-gray-50"
                    >
                      {percent === 100 ? 'Max' : `${percent}%`}
                    </button>
                  ))}
                </div>
              </div>

              {bankDetails.accountHolderName && (
                <div className="p-4 border border-gray-200 rounded-lg">
                  <p className="text-sm font-medium text-gray-700">Withdraw to</p>
                  <div className="flex items-center gap-3 mt-2">
                    <div className="h-10 w-10 rounded-full bg-blue-100 flex items-center justify-center">
                      <Building2 className="h-5 w-5 text-blue-600" />
                    </div>
                    <div>
                      <p className="font-medium text-gray-900">{bankDetails.bankName || 'Bank Account'}</p>
                      <p className="text-sm text-gray-500">Account ending in {bankDetails.iban.slice(-4)}</p>
                    </div>
                  </div>
                </div>
              )}

              {withdrawAmount && parseFloat(withdrawAmount) > 0 && parseFloat(withdrawAmount) < 100 && (
                <p className="text-sm text-red-600">
                  Minimum withdrawal amount is $100.
                </p>
              )}

              <p className="text-xs text-gray-500">
                Withdrawals typically take 7-10 business days to process.
              </p>
            </div>

            <div className="flex gap-3 mt-6">
              <button
                onClick={() => setIsWithdrawOpen(false)}
                className="flex-1 px-4 py-2 border border-gray-300 rounded-lg hover:bg-gray-50"
              >
                Cancel
              </button>
              <button
                onClick={handleWithdraw}
                disabled={!withdrawAmount || parseFloat(withdrawAmount) < 100 || parseFloat(withdrawAmount) > earnings || !bankDetails.accountHolderName}
                className="flex-1 px-4 py-2 bg-gradient-to-r from-purple-600 to-pink-600 text-white rounded-lg hover:opacity-90 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                Withdraw ${withdrawAmount || '0.00'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Add/Edit Bank Details Modal */}
      {isAddBankOpen && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-lg max-w-md w-full p-6 max-h-[90vh] overflow-y-auto">
            <h2 className="text-xl font-bold text-gray-900 mb-2">
              {bankDetails.accountHolderName ? 'Edit Bank Details' : 'Add Bank Account'}
            </h2>
            <p className="text-sm text-gray-500 mb-4">
              {bankDetails.accountHolderName 
                ? 'Update your bank account information for payouts.' 
                : 'Add your bank account details to receive payouts.'}
            </p>
            
            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Account Holder Name *
                </label>
                <input
                  type="text"
                  value={bankDetails.accountHolderName}
                  onChange={(e) => setBankDetails({ ...bankDetails, accountHolderName: e.target.value })}
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent"
                  placeholder="John Doe"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Bank Name
                </label>
                <input
                  type="text"
                  value={bankDetails.bankName}
                  onChange={(e) => setBankDetails({ ...bankDetails, bankName: e.target.value })}
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent"
                  placeholder="e.g., Chase Bank, Bank of America"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  IBAN *
                </label>
                <input
                  type="text"
                  value={bankDetails.iban}
                  onChange={(e) => setBankDetails({ ...bankDetails, iban: e.target.value })}
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent"
                  placeholder="GB29 NWBK 6016 1331 9268 19"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  SWIFT/BIC Code
                </label>
                <input
                  type="text"
                  value={bankDetails.swiftCode}
                  onChange={(e) => setBankDetails({ ...bankDetails, swiftCode: e.target.value })}
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent"
                  placeholder="BOFAUS3N"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Country
                </label>
                <input
                  type="text"
                  value={bankDetails.country}
                  onChange={(e) => setBankDetails({ ...bankDetails, country: e.target.value })}
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent"
                  placeholder="United States"
                />
              </div>
            </div>

            <div className="flex gap-3 mt-6">
              <button
                onClick={() => setIsAddBankOpen(false)}
                className="flex-1 px-4 py-2 border border-gray-300 rounded-lg hover:bg-gray-50"
              >
                Cancel
              </button>
              <button
                onClick={() => {
                  handleSaveBankDetails();
                  setIsAddBankOpen(false);
                }}
                disabled={!bankDetails.accountHolderName || !bankDetails.iban}
                className="flex-1 px-4 py-2 bg-gradient-to-r from-purple-600 to-pink-600 text-white rounded-lg hover:opacity-90 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {bankDetails.accountHolderName ? 'Save Changes' : 'Add Account'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default PayoutsScreen;

